import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { ArrowUpRight, CalendarDays, Landmark, RefreshCw } from "lucide-react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";
import type { ListCompetentCourtsForClaimResponse } from "@upclaim/sdk/src/data-contracts";

import { Can } from "@/components/can";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import type { ClaimWithRelations } from "../../api/use-claim";
import { useCompetentCourts, useSyncClaimProcedure } from "../../api/use-claim-actions";
import { openTribunalDigital } from "../../lib/open-tribunal-digital";
import { formatHearingDate, getNextHearingMeta, getNextHearingTitle } from "./hearing-helpers";
import { HearingSelection } from "./hearing-selection";

export type CompetentJuridiction = ListCompetentCourtsForClaimResponse["airline"];

/** The API returns null for a slot without competent court — the SDK type misses it. */
type CompetentCourtsForClaim = {
  [K in "airline" | "departureAirport" | "arrivalAirport"]: CompetentJuridiction | null;
};

/**
 * Port of overview/courts/Court.vue — tribunal de la procédure en cours :
 * tribunaux compétents, lien Tribunal Digital, synchronisation, prochaine
 * audience et informations externes de la procédure.
 */
export function CourtOverview({ claim }: { claim: ClaimWithRelations }) {
  const procedure = claim.procedure;
  const { data, isPending } = useCompetentCourts(claim.id);
  const syncProcedure = useSyncClaimProcedure(claim.id);

  if (!procedure) {
    return null;
  }

  const competentCourts: CompetentCourtsForClaim | undefined = data;

  // ESCP : compagnie européenne sans tribunal compétent en France.
  const isESCP =
    claim.legalSpecification.effectiveFlight.airline.isEC261 &&
    competentCourts !== undefined &&
    competentCourts.airline === null &&
    competentCourts.departureAirport === null &&
    competentCourts.arrivalAirport === null;

  const nextHearingMeta = getNextHearingMeta(procedure.nextProcedureHearing);
  const nextHearingTitle = getNextHearingTitle(procedure.nextProcedureHearing);

  const hasExternalProcedureInfo = Boolean(
    procedure.statusDispayText || procedure.decisionDisplayText || procedure.decisionDate,
  );

  const jurisdictions = competentCourts
    ? [competentCourts.airline, competentCourts.departureAirport, competentCourts.arrivalAirport]
        .filter((court): court is CompetentJuridiction => court !== null)
        .filter((court, index, courts) => courts.findIndex((c) => c.id === court.id) === index)
    : [];

  return (
    <Card className="mx-2 my-4 gap-0 py-0 text-left">
      <div className="px-6 pt-6 pb-3">
        <div
          className={cn(
            "rounded-md border-2 border-dashed px-6 py-5",
            procedure.nextProcedureHearing && "border-double shadow",
          )}
        >
          <div className="flex items-center justify-between">
            {isPending ? (
              <Skeleton className="h-10 w-64" />
            ) : (
              <div className="flex items-center">
                <Landmark className="h-6 w-6 shrink-0 text-muted-foreground" />
                <div className="mt-0 ml-4">
                  {isESCP && !procedure.juridiction ? (
                    <div className="text-left text-sm text-foreground">
                      <p className="font-medium leading-7">Procédure européenne</p>
                      <p className="pr-12 leading-5 text-muted-foreground">
                        Pas de tribunal Français compétent, mais la compagnie est européenne.
                      </p>
                    </div>
                  ) : competentCourts?.airline === null && !procedure.juridiction ? (
                    <div className="text-left text-sm text-foreground">
                      Aucun tribunal trouvé pour cette compagnie
                    </div>
                  ) : (
                    <div
                      className={cn(
                        "text-left text-sm text-foreground",
                        procedure.juridiction?.name && "font-medium",
                      )}
                    >
                      <span>{procedure.juridiction?.name ?? "Aucune audience enregistrée"}</span>
                      {procedure.identifier ? (
                        <Badge variant="outline" className="ml-2">
                          RG {procedure.identifier}
                        </Badge>
                      ) : null}
                    </div>
                  )}
                  {procedure.juridiction?.schedule ? (
                    <div className="mt-1 text-left text-xs italic text-muted-foreground">
                      Ouverture : {procedure.juridiction.schedule}
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            {!isPending ? (
              <div className="mt-4 flex flex-col items-stretch space-y-1 sm:mt-0 sm:ml-6 sm:flex-shrink-0">
                {procedure.identifier ? (
                  <>
                    <Can permissions={[...UPCLAIM_ROLES, "LAWYER"]}>
                      <Button variant="outline" onClick={() => openTribunalDigital(procedure)}>
                        <ArrowUpRight />
                        Voir l'Affaire
                      </Button>
                    </Can>
                    <Button
                      variant="outline"
                      loading={syncProcedure.isPending}
                      onClick={() => syncProcedure.mutate()}
                    >
                      <RefreshCw />
                      Synchroniser
                    </Button>
                    {procedure.lastDocumentsSyncAt ? (
                      <p className="text-right text-xs italic text-muted-foreground">
                        Dernière synchro il y a{" "}
                        {formatDistanceToNow(new Date(procedure.lastDocumentsSyncAt), { locale: fr })}
                      </p>
                    ) : null}
                  </>
                ) : procedure.juridiction ? (
                  <Button
                    variant="outline"
                    render={
                      // biome-ignore lint/a11y/useAnchorContent: content injected by Button children
                      <a
                        target="_blank"
                        rel="noreferrer"
                        href={`https://www.infogreffe.fr/fiche-greffe/${procedure.juridiction.code}`}
                      />
                    }
                  >
                    <CalendarDays />
                    Calendrier
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>

          {procedure.nextProcedureHearing ? (
            <Alert variant="info" className="mt-6">
              <CalendarDays />
              <AlertTitle>{nextHearingTitle}</AlertTitle>
              {nextHearingMeta?.description ? (
                <AlertDescription>{nextHearingMeta.description}</AlertDescription>
              ) : null}
            </Alert>
          ) : null}

          {hasExternalProcedureInfo ? (
            <div className="mt-6 border-t pt-4">
              <dl className="space-y-2 text-sm">
                {procedure.statusDispayText ? (
                  <div className="flex">
                    <dt className="w-28 shrink-0 text-muted-foreground">Position</dt>
                    <dd className="text-foreground">{procedure.statusDispayText}</dd>
                  </div>
                ) : null}
                {procedure.decisionDisplayText ? (
                  <div className="flex">
                    <dt className="w-28 shrink-0 text-muted-foreground">Décision</dt>
                    <dd className="text-foreground">{procedure.decisionDisplayText}</dd>
                  </div>
                ) : null}
                {procedure.decisionDate ? (
                  <div className="flex">
                    <dt className="w-28 shrink-0 text-muted-foreground">Date</dt>
                    <dd className="text-foreground">{formatHearingDate(procedure.decisionDate)}</dd>
                  </div>
                ) : null}
              </dl>
            </div>
          ) : null}
        </div>
      </div>

      {competentCourts && !procedure.nextProcedureHearing && !procedure.identifier ? (
        <Can permissions={[...UPCLAIM_ROLES, "LAWYER"]}>
          <HearingSelection claim={claim} jurisdictions={jurisdictions} />
        </Can>
      ) : null}
    </Card>
  );
}
